import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

const getApiBaseUrl = () => {
  const url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
  return url.endsWith('/') ? url.slice(0, -1) : url;
};
const API_BASE_URL = getApiBaseUrl();

interface DeletionRequest {
  id: string;
  memberId: string;
  status: string;
  requestedAt: string;
  processedAt: string | null;
  /** Selective deletion; null/empty means the legacy full deletion. */
  scopes: Array<'personal_data' | 'gym_memberships'> | null;
  reason: string | null;
  member: {
    id: string;
    fullName: string | null;
    displayName: string | null;
    phoneNumber: string;
    email: string | null;
    memberGymMappings: Array<{
      gym: { id: string; name: string; displayName: string };
      status: string;
    }>;
  };
}

/** What the member actually asked to have removed. */
const SCOPE_BADGES = (scopes: DeletionRequest['scopes']): string[] => {
  if (!scopes || scopes.length === 0) return ['Full deletion'];
  return scopes.map(s => (s === 'personal_data' ? 'Personal data' : 'Leave gyms'));
};

export default function PrivacyTab({ token }: { token: string }) {
  const [requests, setRequests] = useState<DeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'processed' | 'all'>('pending');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/saas/privacy/deletion-requests?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setRequests(data.data);
    } catch {
      toast.error('Failed to load deletion requests');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleProcess = async (request: DeletionRequest) => {
    if (!confirm(
      `Permanently anonymise data for ${request.member.displayName || request.member.fullName || request.member.phoneNumber}?\n\nThis cannot be undone.`
    )) return;

    setProcessing(request.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/saas/privacy/deletion-requests/${request.id}/process`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Member data anonymised successfully');
        fetchRequests();
      } else {
        toast.error(data.error || 'Failed to process request');
      }
    } catch {
      toast.error('Failed to process request');
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Data Deletion Requests</h2>
          <p className="text-sm text-gray-500 mt-0.5">Members who have requested account and data deletion from the app.</p>
        </div>
        <div className="flex gap-2">
          {(['pending', 'processed', 'all'] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${
                statusFilter === s
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s}
            </button>
          ))}
          <button
            onClick={fetchRequests}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <div className="text-4xl mb-3">🔒</div>
          <p className="text-gray-500 font-medium">No {statusFilter === 'all' ? '' : statusFilter} deletion requests</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Member</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Gym(s)</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Requested scope</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Requested</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {requests.map(req => (
                <tr key={req.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-semibold text-gray-900 text-sm">
                      {req.member.displayName || req.member.fullName || '—'}
                    </div>
                    <div className="text-xs text-gray-500 font-mono">{req.member.phoneNumber}</div>
                    {req.member.email && <div className="text-xs text-gray-400">{req.member.email}</div>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {req.member.memberGymMappings.map(m => (
                        <span key={m.gym.id} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                          {m.gym.displayName || m.gym.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {SCOPE_BADGES(req.scopes).map(b => (
                        <span key={b} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-100">
                          {b}
                        </span>
                      ))}
                    </div>
                    {req.reason && (
                      <div className="text-xs text-gray-400 mt-1 max-w-[220px] truncate" title={req.reason}>
                        “{req.reason}”
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                    {new Date(req.requestedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${
                      req.status === 'pending'
                        ? 'bg-amber-50 text-amber-700 border border-amber-100'
                        : 'bg-green-50 text-green-700 border border-green-100'
                    }`}>
                      {req.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {req.status === 'pending' && (
                      <button
                        onClick={() => handleProcess(req)}
                        disabled={processing === req.id}
                        className="px-4 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        {processing === req.id ? 'Processing...' : 'Delete Data'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-800">
        <strong>Note:</strong> Before processing, contact the member's gym to confirm. Processing honours the requested scope: <em>Personal data</em> anonymises name, phone and email and marks the account deleted; <em>Leave gyms</em> deactivates every gym membership. Transactional records (check-ins, payments) are retained for accounting purposes but are no longer linked to personally identifiable information.
      </div>
    </div>
  );
}
